import React, { useState, useEffect } from 'react';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLocation } from '../../contexts/LocationContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { Asset, AssetCategory, AssetStatus, Employee } from '../../types/api';

const assetSchema = yup.object({
  name: yup.string().required('Asset name is required'),
  category: yup.mixed<AssetCategory>().oneOf(['equipment', 'vehicle', 'building', 'tool', 'machinery', 'electronics', 'other']).required('Category is required'),
  purchase_date: yup.string().required('Purchase date is required'),
  purchase_price: yup.number().required('Purchase price is required').min(0, 'Purchase price must be 0 or greater'),
  current_value: yup.number().nullable().notRequired().transform((value, originalValue) => {
    if (originalValue === '' || originalValue === null || originalValue === undefined || isNaN(Number(originalValue))) {
      return null;
    }
    return Number(originalValue);
  }).test('min-value', 'Current value must be 0 or greater', function(value) {
    return value === null || value === undefined || value >= 0;
  }),
  assigned_to_employee_id: yup.string().nullable().notRequired(),
  status: yup.mixed<AssetStatus>().oneOf(['active', 'inactive', 'maintenance', 'disposed']).required('Status is required'),
  description: yup.string().nullable().notRequired(),
});

type AssetFormData = yup.InferType<typeof assetSchema>;

const AssetForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const isEditMode = !!id;
  const isReadOnly = user?.role === 'user';
  const isAdmin = user?.role === 'admin';

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<AssetFormData>({
    resolver: yupResolver(assetSchema) as unknown as Resolver<AssetFormData>,
    defaultValues: {
      status: 'active',
    },
  });

  useEffect(() => {
    if (isReadOnly) {
      navigate('/assets');
      return;
    }

    // Only admins can edit assets
    if (isEditMode && !isAdmin) {
      navigate('/assets');
      return;
    }

    loadEmployees();
    if (isEditMode) {
      loadAsset();
    }
  }, [id, isEditMode, isReadOnly, isAdmin, selectedLocation?.id]);

  const loadEmployees = async () => {
    if (!selectedLocation?.id) return;
    try {
      // Fetch employees from payroll system - only managers and regular farm labour
      const response = await apiClient.get(`/employees?location_id=${selectedLocation.id}&is_active=true`);
      // Filter to only show managers and regular farm labour (exclude daily wage labour)
      const filteredEmployees = (response.data || []).filter((emp: Employee) => 
        emp.employee_type === 'manager' || emp.employee_type === 'regular_farm_labour'
      );
      setEmployees(filteredEmployees);
    } catch (err) {
      console.error('Error loading employees:', err);
    }
  };

  const loadAsset = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(`/assets/${id}`);
      const asset: Asset = response.data;
      
      setValue('name', asset.name);
      setValue('category', asset.category);
      setValue('purchase_date', asset.purchase_date.split('T')[0]);
      setValue('purchase_price', asset.purchase_price);
      setValue('current_value', asset.current_value || undefined);
      setValue('assigned_to_employee_id', asset.assigned_to_employee_id || '');
      setValue('status', asset.status);
      setValue('description', asset.description || '');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: AssetFormData) => {
    if (!selectedLocation?.id) {
      setError('Please select a location');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payload = {
        ...data,
        location_id: selectedLocation.id,
        current_value: data.current_value ?? null,
        assigned_to_employee_id: data.assigned_to_employee_id || null,
      };

      if (isEditMode) {
        await apiClient.put(`/assets/${id}`, payload);
      } else {
        await apiClient.post('/assets', payload);
      }

      navigate('/assets');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditMode) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <Link
          to="/assets"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Assets
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Asset' : 'Create New Asset'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditMode ? 'Update asset information' : 'Add a new asset to track'}
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="space-y-6">
          {/* Asset Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Asset Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.name ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Tractor, Generator, Shed A"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              {...register('category')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.category ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Select Category</option>
              <option value="equipment">Equipment</option>
              <option value="vehicle">Vehicle</option>
              <option value="building">Building</option>
              <option value="tool">Tool</option>
              <option value="machinery">Machinery</option>
              <option value="electronics">Electronics</option>
              <option value="other">Other</option>
            </select>
            {errors.category && (
              <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
            )}
          </div>

          {/* Purchase Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              {...register('purchase_date')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.purchase_date ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.purchase_date && (
              <p className="mt-1 text-sm text-red-600">{errors.purchase_date.message}</p>
            )}
          </div>

          {/* Purchase Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Purchase Price (₹) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              {...register('purchase_price', { valueAsNumber: true })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.purchase_price ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="0.00"
            />
            {errors.purchase_price && (
              <p className="mt-1 text-sm text-red-600">{errors.purchase_price.message}</p>
            )}
          </div>

          {/* Current Value */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Value (₹)
            </label>
            <input
              type="number"
              step="0.01"
              {...register('current_value', { valueAsNumber: true })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.current_value ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Leave empty to use purchase price"
            />
            {errors.current_value && (
              <p className="mt-1 text-sm text-red-600">{errors.current_value.message}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              If not specified, will default to purchase price
            </p>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              {...register('status')}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.status ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
              <option value="disposed">Disposed</option>
            </select>
            {errors.status && (
              <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
            )}
          </div>

          {/* Assigned To */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assigned To
            </label>
            <select
              {...register('assigned_to_employee_id')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Unassigned</option>
              {employees.map(employee => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} ({employee.employee_code})
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Additional notes or description about the asset..."
            />
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-6 flex justify-end gap-4">
          <Link
            to="/assets"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : isEditMode ? 'Update Asset' : 'Create Asset'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssetForm;

